import { closestPointOnSegment, haversineM, lineDistanceM } from "./geo.ts";
import type { LngLat } from "./types.ts";

/** End-to-start distance that counts as coming back to the start. */
export const LOOP_CLOSE_M = 60;
/** A short scribble whose ends meet is not a loop. */
export const LOOP_MIN_LENGTH_M = 150;

/** On the road / near a road / far from every road. Meters, not a route. */
export const ROAD_ON_M = 20;
export const ROAD_NEAR_M = 50;

export interface SnapResult {
  point: LngLat;
  snapped: boolean;
  distanceM: number;
}

export type RoadBand = "on" | "near" | "far" | "plain";

export interface ColoredSpan {
  coordinates: LngLat[];
  band: RoadBand;
}

/**
 * MapLibre style layer ids that carry walkable/drivable centerlines from
 * OpenMapTiles `transportation` (via OpenFreeMap vector tiles).
 *
 * Liberty uses `road_*` / `bridge_*` / `tunnel_*`.
 * Bright uses `highway-*` / `bridge-*` / `tunnel-*` (and keeps `road_pier`).
 * Proximity coloring queries rendered features on these layers only — if the
 * id pattern misses the active style, every draft segment looks "off-road".
 */
export function isRoadLayer(id: string): boolean {
  if (!/^(road|bridge|tunnel)[_-]|^highway-/.test(id)) return false;
  return !/casing|rail|hatching|one_?way|area|shield|pattern|transit|name/.test(id);
}

export interface GpxTrack {
  name: string | null;
  points: LngLat[];
  /** Which GPX collection the points came from. */
  source: "track" | "route";
}

type RoadSegment = readonly [LngLat, LngLat];

export function dedupeNear(points: LngLat[], epsilonM: number): LngLat[] {
  const out: LngLat[] = [];
  for (const p of points) {
    if (out.length === 0 || haversineM(out[out.length - 1], p) > epsilonM) {
      out.push(p);
    }
  }
  return out;
}

export function isLoopClosed(
  points: LngLat[],
  radiusM = LOOP_CLOSE_M,
  minLengthM = LOOP_MIN_LENGTH_M,
): boolean {
  if (points.length < 3) return false;
  const gap = haversineM(points[0], points[points.length - 1]);
  if (gap > radiusM) return false;
  return lineDistanceM(points) >= minLengthM;
}

export function loopGapM(points: LngLat[]): number | null {
  if (points.length < 2) return null;
  return haversineM(points[0], points[points.length - 1]);
}

/**
 * Persist a closed piece as a LineString whose last coordinate equals the first.
 * An open piece is stored unchanged. `closed` is the editor flag; a line that
 * already meets itself is stored closed even if the flag was omitted.
 */
export function normalizeStoredLine(
  coordinates: LngLat[],
  closedFlag: boolean,
): { coordinates: LngLat[]; closed: boolean } {
  const coords = coordinates.map(clone);
  const geometric = isLoopClosed(coords);
  const closed = closedFlag || geometric;
  if (!closed) return { coordinates: coords, closed: false };
  const gap = haversineM(coords[0], coords[coords.length - 1]);
  if (gap < 1) {
    coords[coords.length - 1] = clone(coords[0]);
    return { coordinates: coords, closed: true };
  }
  return { coordinates: [...coords, clone(coords[0])], closed: true };
}

/** Drop a duplicated closing vertex so the editor can show one start point. */
export function stripClosingVertex(coordinates: LngLat[]): {
  points: LngLat[];
  closed: boolean;
} {
  const points = coordinates.map(clone);
  if (points.length >= 4 && haversineM(points[0], points[points.length - 1]) <= 3) {
    points.pop();
    return { points, closed: true };
  }
  return { points, closed: isLoopClosed(points) };
}

export function snapToRoads(
  point: LngLat,
  segments: readonly RoadSegment[],
  radiusM: number,
  index?: RoadSpatialIndex | null,
): SnapResult {
  const resolved = resolveRoadIndex(segments, index);
  const best = nearestRoad(point, segments, resolved);
  if (!best || best.distanceM > radiusM) {
    return {
      point: clone(point),
      snapped: false,
      distanceM: best?.distanceM ?? Infinity,
    };
  }
  return { point: best.point, snapped: true, distanceM: best.distanceM };
}

export function roadBand(distanceM: number): RoadBand {
  if (distanceM <= ROAD_ON_M) return "on";
  if (distanceM <= ROAD_NEAR_M) return "near";
  return "far";
}

export function distanceToRoadsM(point: LngLat, segments: readonly RoadSegment[]): number {
  const index = segments.length >= 48 ? roadIndex(segments) : null;
  return nearestRoad(point, segments, index)?.distanceM ?? Infinity;
}

const ROAD_CELL_M = 70;

export interface RoadSpatialIndex {
  buckets: Map<string, RoadSegment[]>;
  toX: (lng: number) => number;
  toY: (lat: number) => number;
}

/** Build the road grid once. Callers that pass it skip a rebuild on every segment. */
export function indexRoads(segments: readonly RoadSegment[]): RoadSpatialIndex {
  return roadIndex(segments);
}

function resolveRoadIndex(
  segments: readonly RoadSegment[],
  index: RoadSpatialIndex | null | undefined,
): RoadSpatialIndex | null {
  if (index !== undefined) return index;
  return segments.length >= 48 ? roadIndex(segments) : null;
}

function nearestRoad(
  point: LngLat,
  segments: readonly RoadSegment[],
  index: RoadSpatialIndex | null,
): { point: LngLat; distanceM: number } | null {
  if (segments.length === 0) return null;
  let bestPoint = point;
  let bestDist = Infinity;
  const visit = (seg: RoadSegment) => {
    const hit = closestPointOnSegment(point, seg[0], seg[1]);
    if (hit.distanceM < bestDist) {
      bestDist = hit.distanceM;
      bestPoint = hit.point;
    }
  };
  if (!index) {
    for (const seg of segments) visit(seg);
  } else {
    const cx = Math.floor(index.toX(point[0]));
    const cy = Math.floor(index.toY(point[1]));
    const seen = new Set<RoadSegment>();
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const bucket = index.buckets.get(`${cx + dx}:${cy + dy}`);
        if (!bucket) continue;
        for (const seg of bucket) {
          if (seen.has(seg)) continue;
          seen.add(seg);
          visit(seg);
        }
      }
    }
    // A road just outside the 3×3 window is farther than one cell, so the
    // brute answer can only matter inside the magnet / color radii.
    if (bestDist === Infinity) {
      for (const seg of segments) visit(seg);
    }
  }
  return { point: bestPoint, distanceM: bestDist };
}

function roadIndex(segments: readonly RoadSegment[]): RoadSpatialIndex {
  const lat0 = segments[0][0][1];
  const cos = Math.max(0.2, Math.cos((lat0 * Math.PI) / 180));
  const toX = (lng: number) => (lng * 111_320 * cos) / ROAD_CELL_M;
  const toY = (lat: number) => (lat * 111_320) / ROAD_CELL_M;
  const buckets = new Map<string, RoadSegment[]>();
  for (const seg of segments) {
    const minX = Math.floor(Math.min(toX(seg[0][0]), toX(seg[1][0])));
    const maxX = Math.floor(Math.max(toX(seg[0][0]), toX(seg[1][0])));
    const minY = Math.floor(Math.min(toY(seg[0][1]), toY(seg[1][1])));
    const maxY = Math.floor(Math.max(toY(seg[0][1]), toY(seg[1][1])));
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        const id = `${x}:${y}`;
        const list = buckets.get(id);
        if (list) list.push(seg);
        else buckets.set(id, [seg]);
      }
    }
  }
  return { buckets, toX, toY };
}

/** Split a line into spans colored by how far each part sits from a road. */
export function colorLineByRoads(
  points: LngLat[],
  segments: readonly RoadSegment[],
  index?: RoadSpatialIndex | null,
): ColoredSpan[] {
  if (points.length < 2) return [];
  const resolved = resolveRoadIndex(segments, index);
  const spans: ColoredSpan[] = [];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const midPoint: LngLat = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    // Worst sample wins, so a segment that leaves the road is not painted as on-road.
    const dist = Math.max(
      nearestRoad(a, segments, resolved)?.distanceM ?? Infinity,
      nearestRoad(b, segments, resolved)?.distanceM ?? Infinity,
      nearestRoad(midPoint, segments, resolved)?.distanceM ?? Infinity,
    );
    const band = roadBand(dist);
    const prev = spans[spans.length - 1];
    if (prev && prev.band === band) {
      prev.coordinates.push(clone(b));
    } else {
      spans.push({ band, coordinates: [clone(a), clone(b)] });
    }
  }
  return spans;
}

/**
 * Douglas–Peucker in meters. Keeps the first and last points.
 * Collapses a nearly straight run and leaves real corners.
 */
export function simplifyLine(points: LngLat[], toleranceM: number): LngLat[] {
  if (points.length < 3) return points.map(clone);
  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length > 0) {
    const next = stack.pop();
    if (!next) break;
    const [start, end] = next;
    let maxD = 0;
    let idx = -1;
    for (let i = start + 1; i < end; i++) {
      const d = closestPointOnSegment(points[i], points[start], points[end]).distanceM;
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (idx !== -1 && maxD > toleranceM) {
      keep[idx] = true;
      stack.push([start, idx], [idx, end]);
    }
  }
  return points.filter((_, i) => keep[i]).map(clone);
}

/** Raise the tolerance until the line fits in `maxPoints`. */
export function simplifyToMax(points: LngLat[], toleranceM: number, maxPoints: number): LngLat[] {
  let tolerance = toleranceM;
  let simplified = simplifyLine(dedupeNear(points, 3), tolerance);
  while (simplified.length > maxPoints && tolerance < 500) {
    tolerance *= 1.6;
    simplified = simplifyLine(points, tolerance);
  }
  return simplified;
}

const GPX_POINT_TAGS = ["trkpt", "rtept"] as const;

/**
 * Read a GPX track, or a route when the file has no track points.
 * Points are returned in file order as [lng, lat].
 */
export function parseGpx(xml: string): GpxTrack {
  const text = xml.replace(/^\uFEFF/, "");
  const track = collectGpxPoints(text, "trkpt");
  const route = collectGpxPoints(text, "rtept");
  const points = track.length > 0 ? track : route;
  if (points.length === 0) {
    throw new Error("gpx.empty");
  }
  const name = track.length > 0 ? gpxName(text, "trk") : gpxName(text, "rte");
  return {
    name,
    points,
    source: track.length > 0 ? "track" : "route",
  };
}

function collectGpxPoints(xml: string, tag: (typeof GPX_POINT_TAGS)[number]): LngLat[] {
  const re = new RegExp(`<${tag}\\b([^>]*)\\/?>`, "gi");
  const points: LngLat[] = [];
  for (const match of xml.matchAll(re)) {
    const attrs = match[1] ?? "";
    const lat = attrNumber(attrs, "lat");
    const lon = attrNumber(attrs, "lon");
    if (lat == null || lon == null) continue;
    if (lon < -180 || lon > 180 || lat < -90 || lat > 90) continue;
    points.push([lon, lat]);
  }
  return points;
}

function attrNumber(attrs: string, name: string): number | null {
  const re = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, "i");
  const match = attrs.match(re);
  const raw = match?.[1] ?? match?.[2];
  if (raw == null || raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function gpxName(xml: string, parent: "trk" | "rte"): string | null {
  const block = xml.match(new RegExp(`<${parent}\\b[^>]*>[\\s\\S]*?<\\/${parent}>`, "i"));
  const scope = block?.[0] ?? xml;
  const named = scope.match(/<name>([^<]*)<\/name>/i);
  if (named?.[1]) {
    const text = decodeXml(named[1].trim());
    if (text) return text;
  }
  const meta = xml.match(/<metadata>[\s\S]*?<name>([^<]*)<\/name>/i);
  if (meta?.[1]) {
    const text = decodeXml(meta[1].trim());
    if (text) return text;
  }
  return null;
}

function decodeXml(text: string): string {
  return text
    .replaceAll("&apos;", "'")
    .replaceAll("&quot;", '"')
    .replaceAll("&gt;", ">")
    .replaceAll("&lt;", "<")
    .replaceAll("&amp;", "&");
}

function clone(p: LngLat): LngLat {
  return [p[0], p[1]];
}
